/**
 * The table every list page uses: TanStack Table for sorting, TanStack Virtual for length.
 *
 * Two decisions worth knowing about:
 *
 * 1. It is div-based with explicit ARIA roles, not a <table>. Virtualising real table rows means
 *    absolutely positioning <tr>s, which breaks table layout in every browser. Roles give a
 *    screen reader the same grid semantics without that fight.
 *
 * 2. Virtualisation only switches on above `virtualizeFrom` rows (default 60). Short lists render
 *    plainly, which keeps Ctrl-F, printing and tests honest, and long ones stay at 60fps.
 *
 * Keyboard-first: the grid takes one tab stop, then j/k or the arrow keys move a roving focus and
 * Enter activates. That is how a rep works a queue without reaching for the mouse.
 *
 * Author: Danish Khan
 */
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type RowData,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { EmptyState } from './EmptyState.tsx';

/**
 * Column width, as a CSS grid track (`'96px'`, `'12rem'`). Omit it and the column flexes.
 *
 * This deliberately does NOT use TanStack's `size`: v8 merges a default `size: 150` into every
 * columnDef, so `columnDef.size` is a number for every column and a grid built from it makes
 * nothing flex — every column comes out 150px and the table ends in a wide empty gap.
 */
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    width?: string;
  }
}

export interface DataTableProps<T> {
  data: T[];
  // A column set is heterogeneous by nature; TanStack's own docs use `any` for the value slot.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  rowKey: (row: T) => string;
  ariaLabel: string;
  onRowActivate?: (row: T) => void;
  selectedKey?: string | null;
  estimateRowHeight?: number;
  virtualizeFrom?: number;
  emptyTitle?: string;
  emptyBody?: string;
  /**
   * The request has not answered yet.
   *
   * Callers pass `query.data ?? []`, so a pending request and a genuinely empty result look
   * identical from in here — and the table announced "No handoffs in this view" on first paint,
   * before anything had been asked. On a young tenant, where most answers ARE empty, that reads as
   * a broken product rather than a slow one; on an older one it is a lie that corrects itself half
   * a second later, which is worse, because nobody believes the second version.
   */
  loading?: boolean;
  /** Extra classes for the scroll container. */
  className?: string;
}

export function DataTable<T>({
  data,
  columns,
  rowKey,
  ariaLabel,
  onRowActivate,
  selectedKey,
  estimateRowHeight = 56,
  virtualizeFrom = 60,
  emptyTitle = 'Nothing to show',
  emptyBody,
  loading = false,
  className,
}: DataTableProps<T>): ReactNode {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;
  const virtualize = rows.length > virtualizeFrom;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    overscan: 8,
    enabled: virtualize,
  });

  useEffect(() => {
    if (activeIndex > rows.length - 1) setActiveIndex(Math.max(0, rows.length - 1));
  }, [rows.length, activeIndex]);

  const move = useCallback(
    (delta: number) => {
      setActiveIndex((i) => {
        const next = Math.min(rows.length - 1, Math.max(0, i + delta));
        if (virtualize) virtualizer.scrollToIndex(next, { align: 'auto' });
        return next;
      });
    },
    [rows.length, virtualize, virtualizer],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        move(1);
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        move(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveIndex(Math.max(0, rows.length - 1));
      } else if (e.key === 'Enter' || e.key === ' ') {
        const row = rows[activeIndex];
        if (row && onRowActivate) {
          e.preventDefault();
          onRowActivate(row.original);
        }
      }
    },
    [move, rows, activeIndex, onRowActivate],
  );

  // Nothing is not the same as not-yet. Say so before claiming there is nothing to show.
  if (!data.length && loading) {
    return (
      <div className="px-4 py-10 text-center text-sm text-fg-muted" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }
  if (!data.length) return <EmptyState title={emptyTitle} {...(emptyBody ? { body: emptyBody } : {})} />;

  const headerGroups = table.getHeaderGroups();
  const templateColumns = table
    .getVisibleLeafColumns()
    .map((c) => c.columnDef.meta?.width ?? 'minmax(0, 1fr)')
    .join(' ');

  const renderRow = (index: number, style?: React.CSSProperties): ReactNode => {
    const row = rows[index];
    if (!row) return null;
    const key = rowKey(row.original);
    const selected = selectedKey === key;
    return (
      <div
        key={key}
        role="row"
        aria-rowindex={index + 2}
        aria-selected={selected}
        data-testid="datatable-row"
        data-active={index === activeIndex}
        tabIndex={-1}
        style={{ ...(style ?? {}), gridTemplateColumns: templateColumns }}
        onClick={() => {
          setActiveIndex(index);
          onRowActivate?.(row.original);
        }}
        className={clsx(
          'grid items-center gap-3 border-b border-line px-3 text-sm',
          onRowActivate && 'cursor-pointer',
          selected ? 'h1-row-active' : 'hover:bg-surface-2',
          index === activeIndex && !selected && 'ring-1 ring-inset ring-[var(--h1-focus)]',
        )}
      >
        {row.getVisibleCells().map((cell) => (
          <div key={cell.id} role="gridcell" className="min-w-0 py-2.5">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      ref={scrollRef}
      role="grid"
      aria-label={ariaLabel}
      aria-rowcount={rows.length + 1}
      tabIndex={0}
      onKeyDown={onKeyDown}
      data-virtualized={virtualize}
      /*
        The grid keeps its focus ring. `outline-none` here cancelled the single global
        `:focus-visible` rule on the one `tabIndex={0}` element in the console — so tabbing into
        the table showed nothing at all, and the roving `ring-1` on the active row is not a
        substitute because it is drawn whether or not the grid has focus. The offset is negative so
        the ring is painted inside a container that scrolls, where an outset one would be clipped.
      */
      className={clsx('h1-scroll relative h-full overflow-auto focus-visible:[outline-offset:-2px]', className)}
    >
      <div role="rowgroup" className="sticky top-0 z-10 border-b border-line bg-bg-subtle">
        {headerGroups.map((group) => (
          <div
            key={group.id}
            role="row"
            aria-rowindex={1}
            className="grid items-center gap-3 px-3"
            style={{ gridTemplateColumns: templateColumns }}
          >
            {group.headers.map((header) => {
              const sortable = header.column.getCanSort();
              const dir = header.column.getIsSorted();
              return (
                <div
                  key={header.id}
                  role="columnheader"
                  aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
                  className="py-2 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase"
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      className="flex items-center gap-1 rounded hover:text-fg"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span aria-hidden className="text-[9px] opacity-70">
                        {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}
                      </span>
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div role="rowgroup" className={clsx(virtualize && 'relative w-full')}
        style={virtualize ? { height: `${virtualizer.getTotalSize()}px` } : {}}
      >
        {virtualize
          ? virtualizer.getVirtualItems().map((item) =>
              renderRow(item.index, {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }),
            )
          : rows.map((_, index) => renderRow(index))}
      </div>
    </div>
  );
}
